<script setup lang="ts">
import isImageAsset from "@shared/utils/isImageAsset";

type Slots = {
  content?(): any;
  button?(): any;
};

defineSlots<Slots>();

type Props = {
  asset?: string;
  mobileAsset?: string;
  heading?: string;
  description?: string;
  textPosition?: "center" | "left" | "right";
  size?: "small" | "large" | "originalMedia";
  buttonText?: string;
  linkPath?: string;
  isFiftyFifty?: boolean;
  overlayOpacity?: number;
};

const {
  asset = "",
  mobileAsset = "",
  heading = "",
  description = "",
  textPosition = "center",
  size = "large",
  buttonText = "",
  linkPath = "",
  overlayOpacity = 0,
} = defineProps<Props>();

const { isMobile } = useScreenSize();
const bannerPreset = computed(() => {
  if (size === "originalMedia") return "simpleFormatAndCompression";

  if (isMobile.value) return "squareBannerImage";

  if (size === "small") return "fullWidthBlockDesktopSmall";

  return "fullWidthBlockDesktopLarge";
});

// `mobileImage` is the default, and the `backgroundImage` prop is used to overwrite it on desktop
const assetForCurrentScreen = computed(() => {
  if (isMobile.value) {
    return mobileAsset;
  }

  return asset || mobileAsset;
});

const route = useRoute();

const { fullWidthContentClicked } = useAnalytics();

const onClick = () => {
  fullWidthContentClicked({
    source: route.path,
    target: linkPath,
  });
};

const { getImgixUrlFromStoryblokMedia } = useImgixUrlFromStoryblokMedia();

const isImage = computed(() => isImageAsset(assetForCurrentScreen.value));

const backgroundImage = computed(() =>
  isImage.value && !!assetForCurrentScreen.value
    ? getImgixUrlFromStoryblokMedia(assetForCurrentScreen.value)
    : undefined,
);

const backgroundVideoSources = computed(() => {
  const sources = [];

  if (!!asset && !isImageAsset(asset)) {
    sources.push({
      src: getImgixUrlFromStoryblokMedia(asset),
      type: "video/mp4",
      // we want "mobileAsset" to be the default video source, so we only enable the media query for the desktop video
      media: `(min-width: ${BREAKPOINTS.md}px)`,
    });
  }

  if (!!mobileAsset && !isImageAsset(mobileAsset)) {
    sources.push({
      src: getImgixUrlFromStoryblokMedia(mobileAsset),
      type: "video/mp4",
    });
  }

  return sources;
});

const mediaType = computed(() =>
  backgroundImage.value?.length
    ? "image"
    : backgroundVideoSources.value?.length
      ? "video"
      : "",
);

const enableOverlay = computed(
  () =>
    ((heading && heading.length) || (description && description.length)) &&
    overlayOpacity > 0,
);
</script>

<template>
  <FiftyFiftyBlock
    v-if="isFiftyFifty"
    :background-image="backgroundImage"
    :background-video-sources="backgroundVideoSources"
    :button-text="buttonText"
    :description="description"
    :heading="heading"
    :link-path="linkPath"
    :text-position="textPosition === 'center' ? undefined : textPosition"
  >
    <template
      v-if="$slots.content"
      #content
    >
      <slot name="content" />
    </template>
    <template
      v-if="$slots.button"
      #button
    >
      <slot name="button" />
    </template>
  </FiftyFiftyBlock>
  <div
    v-else
    class="w-viewport"
  >
    <div
      class="full-width-block"
      :class="[
        `full-width-block--${mediaType}`,
        `full-width-block--${mediaType === 'video' ? 'originalMedia' : size}`,
        `full-width-block--${textPosition}`,
        !backgroundImage?.length && !backgroundVideoSources?.length
          ? `full-width-block--text-only`
          : '',
      ]"
    >
      <div
        v-if="assetForCurrentScreen"
        class="full-width-block__background"
        :class="{
          'full-width-block__background--fixed-aspect-ratio':
            size !== 'originalMedia' && mediaType === 'image',
          overlay: enableOverlay,
        }"
        :style="{
          '--overlay-opacity': overlayOpacity,
        }"
      >
        <NuxtImg
          v-if="backgroundImage?.length"
          provider="imgix"
          :preset="bannerPreset"
          loading="lazy"
          :src="backgroundImage"
          sizes="sm:100vw md:100vw lg:100vw xl:100vw 2xl:100vw 3xl:100vw"
          alt="static image"
          class="full-width-block__background--image"
          :class="size !== 'originalMedia' ? 'object-cover' : 'object-contain'"
          @error="setBrokenMediaFallback"
        />

        <BaseVideoPlayer
          v-else-if="backgroundVideoSources?.length"
          :poster="backgroundImage ? backgroundImage : ''"
        >
          <source
            v-for="videoSource in backgroundVideoSources"
            :key="videoSource.src"
            :src="videoSource.src"
            :type="videoSource.type"
            :media="videoSource.media"
          />
        </BaseVideoPlayer>
      </div>
      <div
        v-if="heading || description || linkPath"
        class="full-width-block__content--wrapper"
      >
        <div
          class="full-width-block__content"
          :class="[
            `full-width-block__content--${size}`,
            `full-width-block__content--${textPosition}`,
            assetForCurrentScreen && 'full-width-block__content--has-media',
          ]"
        >
          <slot name="content">
            <h2
              v-if="heading"
              class="full-width-block__content--heading uppercase"
            >
              {{ heading }}
            </h2>
            <p
              v-if="description"
              class="full-width-block__content--description"
            >
              {{ description }}
            </p>

            <div
              v-if="buttonText"
              class="fifty-fifty-block__content--button"
            >
              <slot name="button">
                <BaseNuxtLinkButton
                  v-if="linkPath"
                  :link-path="linkPath"
                  @click="onClick"
                >
                  {{ buttonText }}
                </BaseNuxtLinkButton>
              </slot>
            </div>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.full-width-block {
  @apply bg-platform-bg-1 flex justify-center items-center flex-col text-center relative w-full;

  &--large {
    @apply aspect-square md:aspect-[2/1];
  }

  &--small {
    @apply aspect-square md:aspect-[4/1];
  }

  &--text-only {
    @apply my-0;
    .full-width-block__content {
      @apply py-8;
      p {
        @apply p-0;
      }
    }
  }

  &--left {
    @apply md:items-start md:text-left;
    .full-width-block__content {
      @apply md:pl-28;
    }
  }

  &--center {
    @apply md:items-center md:text-center;
  }

  &--right {
    @apply md:items-end md:text-right;
    .full-width-block__content {
      @apply md:pr-28;
    }
  }

  &__background--fixed-aspect-ratio {
    @apply h-full aspect-square sm:absolute;
  }

  &__background {
    @apply relative w-full;

    &--image {
      @apply h-full object-cover w-full;
    }

    video {
      @apply w-full;
    }
  }

  &__content {
    @apply relative px-3 py-16 w-full md:px-[15%] sm:h-full bg-platform-bg-1 sm:bg-transparent;
    @apply flex flex-col justify-center gap-4;

    &--has-media {
      @apply py-8;
      @apply sm:text-white;
      @apply sm:absolute sm:top-0 sm:left-0;
    }

    &--wrapper {
      @apply w-full flex items-center justify-center;
    }

    &--heading {
      @apply text-3xl sm:text-4xl font-bold;
    }

    &--description {
      @apply text-lg pb-4;
      @apply whitespace-pre-line;
    }

    &--button {
      @apply pt-4;
    }
  }
}

.overlay::before {
  content: "";
  @apply w-full h-full left-0 absolute;
  will-change: transform;
}

@screen sm {
  .overlay::before {
    background-color: rgba(0, 0, 0, var(--overlay-opacity, 0));
  }
}
</style>
